import { createContext, useContext, useId } from "react";
import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

interface FieldContextValue {
  inputId: string;
  hintId: string;
  errorId: string;
  invalid: boolean;
  describedBy: string | undefined;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export function useField(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  label: string;
  /** Guidance shown under the control. Kept short; long guidance belongs in the section intro. */
  hint?: string;
  error?: string;
  required?: boolean;
  /**
   * Keeps the label for screen readers only, where a heading just above
   * already names the field and saying it twice would be noise.
   */
  labelHidden?: boolean;
  /** Rendered at the trailing edge of the label row, e.g. a derived value like age. */
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * Label, control, hint and error as one unit.
 *
 * Labels are sentence case and sit above the control. The label never shouts:
 * weight and colour separate it from the value, not capitals.
 */
export function Field({
  label,
  hint,
  error,
  required = false,
  labelHidden = false,
  aside,
  className,
  children,
}: FieldProps) {
  const base = useId();
  const inputId = `${base}-input`;
  const hintId = `${base}-hint`;
  const errorId = `${base}-error`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined;

  return (
    <FieldContext.Provider
      value={{ inputId, hintId, errorId, invalid: Boolean(error), describedBy }}
    >
      <div className={cn("flex flex-col gap-1.5", className)}>
        <div
          className={cn(
            "flex items-baseline gap-3",
            labelHidden ? "justify-end" : "justify-between",
            labelHidden && !aside && "sr-only",
          )}
        >
          <label
            htmlFor={inputId}
            className={cn("text-xs font-semibold text-fg-muted", labelHidden && "sr-only")}
          >
            {label}
            {required ? (
              <span className="ml-1 text-alert-600" aria-hidden="true">
                *
              </span>
            ) : null}
          </label>
          {aside ? <span className="text-xs text-fg-subtle">{aside}</span> : null}
        </div>

        {children}

        {error ? (
          <p id={errorId} className="text-xs font-medium text-alert-600" role="alert">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-xs text-fg-subtle">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
